import { useCallback, useEffect, useState } from 'react'
import { DEFAULT_OTP_RETRY_SECONDS } from '../../api/mfa'
import { formatCooldown } from '../../platform/formatCooldown'

/**
 * How long "Send a new code" stays unavailable after a code goes out. The
 * backend refuses a second send for the same purpose within this window
 * (`otp_send_limited`), so a shorter wait would invite a refusal.
 */
export const RESEND_COOLDOWN_SECONDS = DEFAULT_OTP_RETRY_SECONDS

/**
 * A countdown for the resend button: `secondsLeft` is 0 when sending is allowed,
 * and `start()` begins it.
 *
 * It is measured against an END TIME, not by counting ticks: a hidden tab's
 * timers are throttled and a sleeping machine runs none, so counting would
 * outlast the server's wait (an hour, in the worst case). The interval only
 * prompts a re-read of the clock; it is cleared at 0 and on unmount.
 */
export function useResendCooldown(seconds: number = RESEND_COOLDOWN_SECONDS) {
  const [state, setState] = useState<{ deadline: number; now: number } | null>(null)
  const secondsLeft = state === null ? 0 : Math.max(Math.ceil((state.deadline - state.now) / 1000), 0)
  const running = secondsLeft > 0

  useEffect(() => {
    if (!running) return
    const id = setInterval(() => setState((s) => (s === null ? s : { ...s, now: Date.now() })), 1000)
    return () => clearInterval(id)
  }, [running])

  /** Begin the wait: the default, or the server's own figure when it named one. */
  const start = useCallback(
    (override?: number) => {
      const now = Date.now()
      setState({ deadline: now + (override ?? seconds) * 1000, now })
    },
    [seconds],
  )
  return { secondsLeft, start }
}

/** The button label for a cooldown in progress. */
export function resendLabel(secondsLeft: number): string {
  return secondsLeft > 0 ? `Send a new code (${formatCooldown(secondsLeft)})` : 'Send a new code'
}

/** The label for a held "Try again" and its kin. */
export function heldLabel(base: string, secondsLeft: number): string {
  return secondsLeft > 0 ? `${base} (${formatCooldown(secondsLeft)})` : base
}
